import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { registerSnackbarCallbacks } from '../utils/snackbarUtils';

// Create a context for the snackbar
const SnackbarContext = createContext();

// Types of snackbars
export const SNACKBAR_TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
};

// Default duration in milliseconds
const DEFAULT_DURATION = 3000;

export const SnackbarProvider = ({ children }) => {
    const [snackbars, setSnackbars] = useState([]);

    // Function to show a snackbar
    const showSnackbar = useCallback((message, type = SNACKBAR_TYPES.INFO, duration = DEFAULT_DURATION) => {
        const id = Date.now() + Math.random().toString(36).substr(2, 9);

        setSnackbars(prev => [...prev, { id, message, type, duration }]);

        // Auto-dismiss after duration
        setTimeout(() => {
            setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
        }, duration);

        return id;
    }, []);

    // Convenience functions for different types
    const showSuccess = useCallback((message, duration) =>
        showSnackbar(message, SNACKBAR_TYPES.SUCCESS, duration), [showSnackbar]);

    const showError = useCallback((message, duration) =>
        showSnackbar(message, SNACKBAR_TYPES.ERROR, duration), [showSnackbar]);

    const showWarning = useCallback((message, duration) =>
        showSnackbar(message, SNACKBAR_TYPES.WARNING, duration), [showSnackbar]);

    const showInfo = useCallback((message, duration) =>
        showSnackbar(message, SNACKBAR_TYPES.INFO, duration), [showSnackbar]);

    // Function to dismiss a snackbar
    const dismissSnackbar = useCallback((id) => {
        setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id));
    }, []);

    // Register callbacks for non-React code
    useEffect(() => {
        registerSnackbarCallbacks({
            showSnackbar,
            showSuccess,
            showError,
            showWarning,
            showInfo
        });
    }, [showSnackbar, showSuccess, showError, showWarning, showInfo]);

    const value = {
        snackbars,
        showSnackbar,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        dismissSnackbar,
    };

    return (
        <SnackbarContext.Provider value={value}>
            {children}
        </SnackbarContext.Provider>
    );
};

// Custom hook to use the snackbar
export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
}; 